/**
 * Single-file dropzone with optional S3 upload for learner media.
 */
import { IconButton, Icon, CircularProgress, Box } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useMemo } from "react";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";

import { uploadToS3 } from "utils/aws_s3";

/**
 * Dropzone that stores a local object URL and optionally uploads to S3.
 *
 * @param {{ className?: string, url?: string, setUrl?: Function, children?: React.ReactNode, accept?: string, showPreview?: boolean, directory?: string, filename?: string, isUpload?: boolean }} props
 */
const VDropZone = (props) => {
  const { className, url, setUrl, children, accept, showPreview, directory, filename, isUpload, ...rest } = props;

  const [loading, setLoading] = useState(false);

  /**
   * Clears the current file URL.
   *
   * @param {React.MouseEvent} e Click event.
   * @returns {void}
   */
  const onRemoveClick = (e) => {
    e.stopPropagation();
    setUrl("");
  };

  /**
   * Accepts a dropped file, stores a preview, and uploads when configured.
   *
   * @param {Array<File>} acceptedFiles Dropped files.
   * @returns {void}
   */
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      const file = acceptedFiles[0];

      if (isUpload && directory) {
        setLoading(true);
        const newFileName = filename || file.name;
        const oldFileName = url ? url.split("/").pop().split("?")[0] : "";
        uploadToS3(file, directory, newFileName, oldFileName)
          .then((publicUrl) => {
            const href = String(publicUrl || "");
            const queued = href.startsWith("blob:");
            if (!href || (!href.startsWith("http") && !queued)) throw new Error("Missing public URL");
            setUrl(queued ? href : `${href}?${Date.now()}`);
          })
          .catch(() => setUrl(null))
          .finally(() => setLoading(false));
        return;
      }

      setUrl(URL.createObjectURL(file));
      setLoading(true);
      setTimeout(() => setLoading(false), 1000);
    },
    [directory, filename, isUpload, setUrl, url]
  );

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: { [accept]: [] },
    maxFiles: 1,
  });

  const style = useMemo(
    () => ({
      ...(isFocused ? { borderColor: "#2196F3" } : {}),
      ...(isDragAccept ? { borderColor: "#00e676" } : {}),
      ...(isDragReject ? { borderColor: "#FF1744" } : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <>
      <Box {...getRootProps({ style })} className={`${className} border-2 border-gray-400 border-dashed flex justify-center items-center cursor-pointer relative v-dropzone`} {...rest} sx={{ borderRadius: "12px" }}>
        {url && showPreview && (
          <>
            <Box className="absolute top-0 left-0 w-full h-full flex justify-center">
              <img src={url} alt="" className="h-full" />
            </Box>
            <IconButton className="absolute top-0 right-0" onClick={onRemoveClick}>
              <Icon>cancel</Icon>
            </IconButton>
          </>
        )}
        {loading && (
          <Box className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-blue-100 bg-opacity-90">
            <CircularProgress color="info" size={80} />
          </Box>
        )}
        <input {...getInputProps()} accept={accept} />
        {url ? <></> : children}
      </Box>
    </>
  );
};

VDropZone.defaultProps = {
  className: "",
  url: "",
  setUrl: () => {},
  accept: "image/*",
  showPreview: true,
  directory: "",
  filename: "",
  isUpload: false,
};

VDropZone.propTypes = {
  className: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
  accept: PropTypes.any.isRequired,
  setUrl: PropTypes.func.isRequired,
  showPreview: PropTypes.bool.isRequired,
  directory: PropTypes.string,
  filename: PropTypes.string,
  isUpload: PropTypes.bool,
};

export default VDropZone;
